import { useState, useEffect } from "react";
import SharedHeader from "@/components/SharedHeader";
import LandingFooter from "@/components/landing/LandingFooter";
import FloatingContactButtons from "@/components/FloatingContactButtons";
import MobileBottomNav from "@/components/landing/MobileBottomNav";
import { useApp } from "@/lib/AppContext";
import { base44 } from "@/api/base44Client";

export default function ContactPage() {
  const { lang } = useApp();
  const isAr = lang === "ar";
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  // Local SEO: structured data + meta for the Contact / Location page
  useEffect(() => {
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "LaundryOrDryCleaningBusiness",
      "@id": "https://moon-laundry.com/contact#business",
      "name": "مغاسل القمر الأبيض",
      "alternateName": "White Moon Laundry",
      "image": "https://media.base44.com/images/public/user_69729b1c2410c5c031f1d29a/291b90bb1_.png",
      "url": "https://moon-laundry.com",
      "telephone": "+966504175289",
      "email": "w.moon.laundry@gmail.com",
      "priceRange": "$$",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "حي 7134، EKJD3183، 3183",
        "addressLocality": "الخبر",
        "addressRegion": "المنطقة الشرقية",
        "postalCode": "34446",
        "addressCountry": "SA"
      },
      "geo": { "@type": "GeoCoordinates", "latitude": 26.2926, "longitude": 50.1920 },
      "hasMap": "https://maps.app.goo.gl/B8i6wD93Cdy8tWvJ9",
      "openingHoursSpecification": [{
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Saturday", "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "06:00",
        "closes": "23:59"
      }],
      "areaServed": { "@type": "City", "name": "Al Khobar" }
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "contact-jsonld";
    script.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    const prevTitle = document.title;
    document.title = isAr ? "تواصل معنا - مغاسل القمر الأبيض | الخبر" : "Contact Us - White Moon Laundry | Al Khobar";
    let metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc ? metaDesc.content : "";
    const newDesc = isAr
      ? "موقع مغاسل القمر الأبيض في الخبر - تواصل معنا هاتفياً أو عبر واتساب أو زورنا في موقعنا. استلام وتوصيل مجاني للملابس والمفروشات والعبايات."
      : "White Moon Laundry location in Al Khobar — contact us by phone or WhatsApp, or visit us. Free pickup and delivery for clothes, abayas and furnishings.";
    if (!metaDesc) { metaDesc = document.createElement("meta"); metaDesc.name = "description"; document.head.appendChild(metaDesc); }
    metaDesc.content = newDesc;

    return () => {
      script.remove();
      document.title = prevTitle;
      if (prevDesc !== "") metaDesc.content = prevDesc;
    };
  }, [isAr]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setSending(true);
    try {
      await base44.integrations.Core.SendEmail({
        to: "w.moon.laundry@gmail.com",
        subject: isAr ? `رسالة من ${form.name}` : `Message from ${form.name}`,
        body: `الاسم / Name: ${form.name}\nالبريد / Email: ${form.email}\n\n${form.message}`,
      });
      setSent(true);
      setForm({ name: "", email: "", message: "" });
    } catch (err) { /* bubble */ }
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-background" dir={isAr ? "rtl" : "ltr"}>
      <SharedHeader />
      <div className="pt-16 pb-24 md:pb-0">
        <section className="py-16">
          <div className="max-w-3xl mx-auto px-4 space-y-8">
            <div className="text-center">
              <img
                src="https://media.base44.com/images/public/user_69729b1c2410c5c031f1d29a/291b90bb1_.png"
                alt="logo"
                className="w-24 h-24 object-contain mx-auto mb-4"
              />
              <h1 className="text-3xl sm:text-4xl font-black text-foreground mb-2">
                {isAr ? "تواصل معنا" : "Contact Us"}
              </h1>
              <p className="text-muted-foreground text-sm">
                {isAr ? "نحن هنا لمساعدتك — تواصل معنا في أي وقت" : "We're here to help — reach out anytime"}
              </p>
            </div>

            {/* Contact methods */}
            <div className="grid sm:grid-cols-3 gap-3">
              <a href="tel:0504175289"
                className="group flex items-center gap-3 bg-card border border-border rounded-2xl p-3.5 hover:shadow-md hover:border-primary/40 transition-all">
                <span className="flex items-center justify-center w-11 h-11 shrink-0 rounded-xl bg-primary/10 text-xl group-hover:scale-110 transition-transform">📞</span>
                <span className="min-w-0 flex flex-col leading-tight">
                  <span className="text-[11px] font-bold text-muted-foreground">{isAr ? "اتصل بنا" : "Call Us"}</span>
                  <span className="text-primary text-sm font-black truncate" dir="ltr">0504175289</span>
                </span>
              </a>
              <a href="https://wa.me/966504175289" target="_blank" rel="noopener noreferrer"
                className="group flex items-center gap-3 bg-card border border-border rounded-2xl p-3.5 hover:shadow-md hover:border-green-500/40 transition-all">
                <span className="flex items-center justify-center w-11 h-11 shrink-0 rounded-xl bg-green-500/10 text-xl group-hover:scale-110 transition-transform">💬</span>
                <span className="min-w-0 flex flex-col leading-tight">
                  <span className="text-[11px] font-bold text-muted-foreground">{isAr ? "واتساب" : "WhatsApp"}</span>
                  <span className="text-green-600 text-sm font-black truncate" dir="ltr">0504175289</span>
                </span>
              </a>
              <a href="mailto:w.moon.laundry@gmail.com"
                className="group flex items-center gap-3 bg-card border border-border rounded-2xl p-3.5 hover:shadow-md hover:border-primary/40 transition-all">
                <span className="flex items-center justify-center w-11 h-11 shrink-0 rounded-xl bg-accent/10 text-xl group-hover:scale-110 transition-transform">✉️</span>
                <span className="min-w-0 flex flex-col leading-tight">
                  <span className="text-[11px] font-bold text-muted-foreground">{isAr ? "البريد" : "Email"}</span>
                  <span className="text-primary text-sm font-black truncate" dir="ltr">w.moon.laundry@gmail.com</span>
                </span>
              </a>
            </div>

            {/* Location map */}
            <div className="bg-card border border-border rounded-2xl overflow-hidden">
              <div className="px-6 pt-5 pb-3 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="font-black text-foreground text-lg flex items-center gap-2">
                    <span aria-hidden="true">📍</span>{isAr ? "موقعنا الجغرافي" : "Our Location"}
                  </h2>
                  <p className="text-muted-foreground text-xs mt-1 max-w-md">
                    {isAr ? "حي 7134، الخبر 34446، المنطقة الشرقية، السعودية" : "District 7134, Al Khobar 34446, Eastern Province, Saudi Arabia"}
                  </p>
                </div>
                <a href="https://maps.app.goo.gl/B8i6wD93Cdy8tWvJ9" target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground font-bold text-xs px-4 py-2 rounded-full hover:opacity-90 transition-all shrink-0">
                  {isAr ? "الاتجاهات 🧭" : "Directions 🧭"}
                </a>
              </div>
              <div className="relative w-full h-72 sm:h-96 bg-secondary">
                <iframe
                  title={isAr ? "موقع مغاسل القمر الأبيض على الخريطة" : "White Moon Laundry location map"}
                  src="https://maps.google.com/maps?q=75VR%2B3R+Al+Khobar+Saudi+Arabia&z=16&hl=ar&output=embed"
                  className="absolute inset-0 w-full h-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </div>

            {/* Contact form */}
            <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
              <h2 className="font-black text-foreground text-lg">
                {isAr ? "أرسل رسالة" : "Send a Message"}
              </h2>
              {sent ? (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-5 text-center">
                  <p className="text-3xl mb-2">✅</p>
                  <p className="font-bold text-green-700 dark:text-green-400">
                    {isAr ? "تم إرسال رسالتك بنجاح!" : "Your message has been sent!"}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-3">
                  <div>
                    <label className="text-xs font-bold text-muted-foreground block mb-1">
                      {isAr ? "الاسم" : "Name"}
                    </label>
                    <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      required className="w-full bg-secondary border border-border rounded-xl px-3 py-2.5 text-sm outline-none focus:border-primary text-foreground" />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-muted-foreground block mb-1">
                      {isAr ? "البريد الإلكتروني" : "Email"}
                    </label>
                    <input type="email" dir="ltr" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                      required className="w-full bg-secondary border border-border rounded-xl px-3 py-2.5 text-sm outline-none focus:border-primary text-foreground" />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-muted-foreground block mb-1">
                      {isAr ? "الرسالة" : "Message"}
                    </label>
                    <textarea value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                      required rows={4} className="w-full bg-secondary border border-border rounded-xl px-3 py-2.5 text-sm outline-none focus:border-primary text-foreground resize-none" />
                  </div>
                  <button type="submit" disabled={sending}
                    className="w-full bg-primary text-primary-foreground font-bold py-3 rounded-xl text-sm hover:opacity-90 disabled:opacity-60 transition-all">
                    {sending ? (isAr ? "⏳ جاري الإرسال..." : "⏳ Sending...") : (isAr ? "إرسال" : "Send")}
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>
        <LandingFooter />
        <FloatingContactButtons />
      </div>
      <MobileBottomNav />
    </div>
  );
}